// biome-ignore-all lint/a11y: TODO - address a11y issues later
// biome-ignore-all lint/suspicious/noExplicitAny: TODO - address later
import { InfoCircledIcon } from "@radix-ui/react-icons";
import { GripVertical } from "lucide-react";
import { Badge } from "@/components/ui/badge";
import { Card, CardContent, CardHeader } from "@/components/ui/card";
import { InfoTooltip } from "@/components/ui/info-tooltip";
import { CATEGORY_STYLES } from "./styles";
import type { BaseMetricCardProps } from "./types";

export function MetricCard<T = Record<string, unknown>>({
  id,
  title,
  value,
  isLoading,
  onDrilldown,
  isWip,
  tooltip,
  category,
  isDragging,
  isDraggingOverlay = false,
  attributes,
  listeners,
  setNodeRef,
  metrics,
}: BaseMetricCardProps<T>) {
  const categoryStyle = CATEGORY_STYLES[category];

  return (
    <div
      ref={setNodeRef}
      className={
        isDragging
          ? "relative rounded-lg border-2 border-dashed border-muted-foreground/25 h-[160px] sm:h-[170px] md:h-[165px] lg:h-[160px] bg-background"
          : ""
      }
    >
      <div className={isDragging ? "invisible" : ""}>
        <Card
          className={`group relative h-[160px] sm:h-[170px] md:h-[165px] lg:h-[160px] overflow-hidden ${
            isDraggingOverlay
              ? "cursor-grabbing shadow-md opacity-90"
              : "hover:shadow-md transition-shadow duration-200"
          }`}
          onClick={isDragging ? undefined : onDrilldown}
        >
          <div className="cursor-pointer hover:bg-accent/30 h-full flex flex-col">
            <CardHeader className="pb-1 pt-4 px-4 flex-shrink-0">
              <div className="flex items-start gap-1.5">
                <div className="text-xs sm:text-[11px] md:text-[10px] lg:text-xs font-medium text-muted-foreground uppercase tracking-wide line-clamp-2 h-7 sm:h-8 md:h-7 lg:h-8 overflow-hidden leading-tight">
                  {title}
                  {tooltip && (
                    <InfoTooltip
                      trigger={
                        <button
                          className="text-muted-foreground hover:text-foreground transition-colors inline-block ml-1 align-text-top"
                          aria-label={`Info about ${title}`}
                        >
                          <InfoCircledIcon className="h-3.5 w-3.5" />
                        </button>
                      }
                      side="right"
                      className="z-50 w-72 rounded-lg border bg-popover p-4 text-popover-foreground shadow-md outline-none"
                      sideOffset={5}
                    >
                      <p className="text-sm">{tooltip}</p>
                    </InfoTooltip>
                  )}
                </div>
                {isWip && (
                  <Badge
                    variant="secondary"
                    className="text-xs px-2 py-0.5 bg-amber-100 text-amber-800 rounded-full font-medium shrink-0"
                  >
                    WIP
                  </Badge>
                )}
              </div>
            </CardHeader>

            <CardContent className="flex-grow px-4 pt-2 pb-4">
              <div className="h-full flex flex-col">
                {/* Main value with responsive sizing */}
                <div className="flex items-center mb-1">
                  {isLoading ? (
                    <div className="h-8 w-20 animate-pulse rounded bg-muted" />
                  ) : (
                    <div className="flex items-center">
                      <span className="text-2xl sm:text-3xl md:text-2xl lg:text-3xl font-bold leading-none">
                        {value}
                      </span>
                    </div>
                  )}
                </div>

                {/* Secondary labels with simple truncation */}
                <div className="text-sm sm:text-base md:text-sm lg:text-base text-muted-foreground/50 min-h-[20px] sm:min-h-[24px] md:min-h-[20px] lg:min-h-[24px] flex items-start">
                  {/* Ratio metrics with values and simple truncation */}
                  {id === "engagements_per_account" && metrics && (
                    <div className="w-full overflow-hidden">
                      <span className="text-xs sm:text-sm md:text-xs lg:text-sm text-muted-foreground/70 leading-tight truncate whitespace-nowrap">
                        <span className="font-medium">
                          {(
                            (metrics as any).engagements_per_account as number
                          )?.toFixed(1) || "0.0"}
                        </span>
                        {" engagements/account"}
                      </span>
                    </div>
                  )}

                  {id === "engagements_per_person" && metrics && (
                    <div className="w-full overflow-hidden">
                      <span className="text-xs sm:text-sm md:text-xs lg:text-sm text-muted-foreground/70 leading-tight truncate whitespace-nowrap">
                        <span className="font-medium">
                          {(
                            (metrics as any).engagements_per_person as number
                          )?.toFixed(1) || "0.0"}
                        </span>
                        {" engagements/person"}
                      </span>
                    </div>
                  )}

                  {id === "engagements_per_deal_contact" && metrics && (
                    <div className="w-full overflow-hidden">
                      <span className="text-xs sm:text-sm md:text-xs lg:text-sm text-muted-foreground/70 leading-tight truncate whitespace-nowrap">
                        <span className="font-medium">
                          {(
                            (metrics as any)
                              .engagements_per_deal_contact as number
                          )?.toFixed(1) || "0.0"}
                        </span>
                        {" engagements/contact"}
                      </span>
                    </div>
                  )}

                  {/* Standard metrics don't need additional labels here */}
                </div>
                {/* eslint-enable @typescript-eslint/no-explicit-any */}
              </div>
            </CardContent>

            {/* Category strip at the bottom with text on right */}
            <div
              className={`absolute bottom-0 left-0 right-0 py-1.5 px-4 flex items-center justify-end ${categoryStyle.color}`}
            >
              <div className="flex items-center gap-1.5">
                {categoryStyle.icon}
                <span className="text-[10px] sm:text-[9px] md:text-[8px] lg:text-[10px] font-medium uppercase leading-none">
                  {category}
                </span>
              </div>
            </div>
          </div>

          {/* Drag handle for normal mode */}
          {attributes && listeners && !isDraggingOverlay && (
            <div
              {...attributes}
              {...listeners}
              className={`absolute top-2 right-2 flex h-6 w-6 items-center justify-center text-muted-foreground opacity-0 transition-opacity group-hover:opacity-100 ${
                isDragging ? "cursor-grabbing" : "cursor-grab"
              }`}
            >
              <GripVertical className="h-4 w-4" />
            </div>
          )}

          {/* Drag handle for overlay mode */}
          {isDraggingOverlay && (
            <div className="absolute top-2 right-2 flex h-6 w-6 items-center justify-center text-muted-foreground cursor-grabbing">
              <GripVertical className="h-4 w-4" />
            </div>
          )}
        </Card>
      </div>
    </div>
  );
}
